import { supabase, DEMO_MODE } from './supabase';

export type RedeemReason =
  | 'redeemed'
  | 'already_active'
  | 'not_found'
  | 'inactive'
  | 'expired'
  | 'sold_out'
  | 'no_auth'
  | 'error';

export interface RedeemResult {
  ok: boolean;
  reason: RedeemReason;
}

// Calls the redeem_coupon SECURITY DEFINER RPC. On success the caller's profile
// is flipped to subscription_status='active' server-side; the UI then reloads
// from cloud to pick up the new entitlement.
export async function redeemCoupon(code: string): Promise<RedeemResult> {
  const clean = code.trim();
  if (!clean) return { ok: false, reason: 'error' };
  if (DEMO_MODE || !supabase) return { ok: false, reason: 'error' };

  const { data, error } = await supabase.rpc('redeem_coupon', { p_code: clean });
  if (error || !data) return { ok: false, reason: 'error' };

  const reason = (data as { reason?: RedeemReason }).reason ?? 'error';
  const ok = Boolean((data as { ok?: boolean }).ok);
  return { ok, reason };
}

export function redeemMessage(reason: RedeemReason, isHe: boolean): string {
  const en: Record<RedeemReason, string> = {
    redeemed: 'Code applied! Free access unlocked. Refreshing…',
    already_active: "You've already redeemed this code — access is active. Refreshing…",
    not_found: "That code isn't valid.",
    inactive: 'This code is no longer active.',
    expired: 'This code has expired.',
    sold_out: 'This code has reached its redemption limit.',
    no_auth: 'Please sign in first, then enter the code.',
    error: 'Something went wrong. Please try again.',
  };
  const he: Record<RedeemReason, string> = {
    redeemed: 'הקוד הופעל! גישה חינם נפתחה. מרענן…',
    already_active: 'כבר מימשת את הקוד הזה — הגישה פעילה. מרענן…',
    not_found: 'הקוד לא תקין.',
    inactive: 'הקוד כבר אינו פעיל.',
    expired: 'תוקף הקוד פג.',
    sold_out: 'הקוד הגיע למכסת המימושים.',
    no_auth: 'יש להתחבר תחילה ואז להזין את הקוד.',
    error: 'משהו השתבש. נסה שוב.',
  };
  return (isHe ? he : en)[reason];
}

// Shared disclaimer copy (paywall + /redeem page).
export function couponDisclaimer(isHe: boolean): string {
  return isHe
    ? 'גישה באמצעות קוד קופון היא הטבה קידום מכירות הניתנת כפי שהיא (as-is). הגישה נשארת פעילה עד להודעה חדשה וניתן לבטלה או לסיימה בכל עת לפי שיקול דעת הבעלים, ללא הודעה מוקדמת וללא החזר כספי.'
    : 'Coupon access is a promotional benefit provided as-is. It stays active until further notice and may be revoked or ended at any time at the owner’s sole discretion, without prior notice or refund.';
}
